import {
  Box,
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { SadFaceIcon } from "src/assets/icons/sad-face";
import { LoadingPlaceholder } from "../loading-placeholder/loading-placeholder";
import { classes } from "./data-table.styles";
import { OrderBy } from "./use-data-table";

type Data = Record<string, any>;

export type ColumnConfig<D> = {
  key: string;
  sortKey?: string;
  title: string | React.ReactNode;
  component?: TableCellProps["component"];
  width?: string | number;
  render: (item: D) => React.ReactNode;
  skip?: boolean;
  maxWidth?: number;
  notSortable?: boolean;
};

type Props<D extends Data> = {
  loading?: boolean;
  data: D[];
  columns: ColumnConfig<D>[];
  rowIdKey?: keyof D;
  emptyText: string;
  itemsCount: number;
  page: number;
  sort: OrderBy;
  onPageChange: (page: number) => void;
  onSortChange: (sort: OrderBy) => void;
  onPrefetch: (page: number | null) => void;
};

export const pageLimit = 6;

export const DataTable = <T extends Data>({
  data,
  columns,
  loading,
  rowIdKey = "id",
  emptyText,
  itemsCount,
  page,
  sort,
  onPageChange,
  onSortChange,
  onPrefetch,
}: Props<T>) => {
  const pagesCount = useMemo(
    () => Math.ceil(itemsCount / pageLimit),
    [itemsCount]
  );

  const renderedColumns = useMemo(
    () => columns.filter((c) => !c.skip),
    [columns]
  );

  const handleRequestSort = useCallback(
    (property: string) => {
      const isAsc = sort.columnName === property && sort.direction === "asc";

      const newSort: OrderBy = {
        direction: isAsc ? "desc" : "asc",
        columnName: property,
      };

      onSortChange(newSort);
    },
    [onSortChange, sort.columnName, sort.direction]
  );

  return (
    <Box sx={classes.container}>
      <Table sx={classes.table}>
        <TableHead>
          <TableRow>
            {renderedColumns.map((column) => (
              <TableCell
                key={column.key}
                variant="head"
                sx={classes.cellHeader}
                sortDirection={
                  sort.columnName === column.key ? sort.direction : false
                }
              >
                {column.notSortable ? (
                  <Box sx={classes.headerLabel}>{column.title}</Box>
                ) : (
                  <TableSortLabel
                    sx={classes.headerLabel}
                    active={
                      sort.columnName === column.sortKey ||
                      sort.columnName === column.key
                    }
                    direction={
                      sort.columnName === column.sortKey ||
                      sort.columnName === column.key
                        ? sort.direction
                        : "asc"
                    }
                    onClick={() =>
                      handleRequestSort(column.sortKey || column.key)
                    }
                  >
                    {column.title}
                  </TableSortLabel>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {data.map((row, index) => (
            <TableRow sx={classes.row} key={row[rowIdKey]}>
              {renderedColumns.map((column) => (
                <TableCell
                  sx={classes.cell}
                  key={column.key}
                  size="small"
                  variant="body"
                  component={column.component}
                  style={{ width: column.width }}
                >
                  <>
                    <Typography
                      sx={classes.text}
                      component="span"
                      style={{ maxWidth: column.maxWidth }}
                    >
                      {column.render(row)}
                    </Typography>
                  </>
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {data.length > 0 && (
        <Pagination
          sx={classes.pagination}
          count={pagesCount}
          onChange={(event, newPage) => onPageChange(newPage)}
          shape="rounded"
          color="primary"
          page={page}
          renderItem={(item) => (
            <PaginationItem
              onMouseEnter={() => {
                onPrefetch(item.page);
              }}
              {...item}
            />
          )}
        />
      )}

      {loading && (
        <Box sx={classes.loading}>
          <LoadingPlaceholder />
        </Box>
      )}

      {data.length === 0 && !loading && (
        <Box sx={classes.empty}>
          <SadFaceIcon />
          <Typography>{emptyText}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default DataTable;
